import { INTERACTION } from 'app/shopify/generated/interaction';
import { useState } from 'react';
import { TextInput, type TextInputProps } from 'react-native';
import { IconSearch, IconX } from '@tabler/icons-react-native';
import { Box, useTheme } from 'app/shopify/theme';
import { NavigationPressable } from '../Navigation/NavigationPressable';
import { fieldInputReset } from '../../forms/field.styles';

export interface HeaderSearchProps {
  value: string;
  onChangeText: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder: string;
  accessibilityLabel: string;
  submitLabel: string;
  /** Show a clear action for non-empty queries when a localized label is supplied. */
  clearLabel?: string;
  onFocus?: TextInputProps['onFocus'];
  onBlur?: TextInputProps['onBlur'];
  disabled?: boolean;
}

export function HeaderSearch({
  value,
  onChangeText,
  onSubmit,
  placeholder,
  accessibilityLabel,
  submitLabel,
  clearLabel,
  onFocus,
  onBlur,
  disabled = false,
}: HeaderSearchProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const fieldSize = Number(theme.tokens.sizing['sizing.general.x12']);
  const buttonSize = Number(theme.tokens.sizing['sizing.general.x8']);
  const typography = theme.textVariants.bodyBaseMedium;
  const submit = () => {
    if (!disabled) onSubmit(value);
  };
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      backgroundColor="surface.default"
      borderRadius="radius.full"
      minHeight={fieldSize}
      style={{ opacity: disabled ? INTERACTION.opacity['040'] : INTERACTION.opacity['100'] }}
    >
      <TextInput
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled }}
        value={value}
        onChangeText={onChangeText}
        onSubmitEditing={submit}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        editable={!disabled}
        placeholder={placeholder}
        placeholderTextColor={theme.colors['text.tertiary']}
        returnKeyType="search"
        style={[
          fieldInputReset.input,
          {
            minHeight: fieldSize,
            paddingLeft: theme.spacing['spacing.general.x6'],
            paddingVertical: theme.spacing['spacing.general.x3'],
            fontFamily: typography.fontFamily,
            fontSize: typography.fontSize,
            fontWeight: typography.fontWeight,
            lineHeight: typography.lineHeight,
            letterSpacing: typography.letterSpacing,
            color: theme.colors['text.primary'],
          },
        ]}
      />
      {clearLabel && value.length > 0 && (
        <NavigationPressable
          accessibilityLabel={clearLabel}
          onPress={() => onChangeText('')}
          disabled={disabled}
          style={{
            width: buttonSize,
            height: buttonSize,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.borderRadii['radius.full'],
          }}
        >
          <IconX
            size={Number(theme.tokens.sizing['sizing.icon.s'])}
            color={theme.colors['icon.primary']}
            strokeWidth={2}
          />
        </NavigationPressable>
      )}
      <Box width={fieldSize} height={fieldSize} flexShrink={0} alignItems="center" justifyContent="center">
        <NavigationPressable
          accessibilityLabel={submitLabel}
          onPress={submit}
          disabled={disabled}
          style={{
            width: buttonSize,
            height: buttonSize,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.borderRadii['radius.full'],
            backgroundColor: theme.colors['icon.primary'],
          }}
        >
          <IconSearch
            size={Number(theme.tokens.sizing['sizing.icon.s'])}
            color={theme.colors['icon.inverse.primary']}
            strokeWidth={2}
          />
        </NavigationPressable>
      </Box>
      <Box
        pointerEvents="none"
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        right={0}
        borderRadius="radius.full"
        borderWidth={theme.borderWidths['border-width.subtle']}
        borderColor={focused ? 'border.focused' : 'border.secondary'}
      />
    </Box>
  );
}
